/**
 * Workflow Registry — seed definitions for v1.
 *
 * Inline TypeScript, not JSON files — full type safety, Zod-validated at import time.
 * These are the built-in workflow templates. User-authored workflows come in v2.
 */

import { WorkflowDefinition, workflowDefinitionSchema } from "./workflow";

const rawDefinitions: WorkflowDefinition[] = [
  {
    id: "wf-daily-standup",
    name: "Daily Standup",
    description: "Compile yesterday's completed tasks, today's priorities, and active blockers into a standup summary.",
    category: "reporting",
    domain: "tasks",
    version: 1,
    iconKey: "clock",
    preflight: ["requires_org_context"],
    steps: [
      { kind: "specialist_call", label: "Fetch completed tasks", specialist: "task-management", inputs: { action: "list_completed", period: "yesterday" } },
      { kind: "specialist_call", label: "Fetch today's priorities", specialist: "task-management", inputs: { action: "list_today" } },
      { kind: "specialist_call", label: "Identify blockers", specialist: "task-management", inputs: { action: "list_blocked" } },
      { kind: "specialist_call", label: "Generate standup summary", specialist: "generate-report", inputs: { format: "standup" } },
    ],
  },
  {
    id: "wf-client-onboarding",
    name: "Client Onboarding",
    description: "Walk through new client intake: CRM record creation, project setup, and welcome sequence.",
    category: "automation",
    domain: "clients",
    version: 1,
    iconKey: "briefcase",
    preflight: ["requires_org_context"],
    steps: [
      { kind: "specialist_call", label: "Create CRM record", specialist: "manage-client" },
      { kind: "specialist_call", label: "Create onboarding project", specialist: "manage-pipeline" },
      { kind: "emit_event", label: "Trigger welcome sequence", domain: "crm", type: "client_onboarding_started" },
      { kind: "specialist_call", label: "Schedule first meeting", specialist: "manage-client", inputs: { action: "schedule_kickoff" } },
    ],
  },
  {
    id: "wf-weekly-review",
    name: "Weekly Review",
    description: "Compile team velocity, completed tasks, missed deadlines, and improvement suggestions.",
    category: "reporting",
    domain: "analytics",
    version: 1,
    iconKey: "bar-chart",
    preflight: ["requires_org_context"],
    steps: [
      { kind: "specialist_call", label: "Pull weekly task metrics", specialist: "task-management", inputs: { action: "weekly_summary" } },
      { kind: "specialist_call", label: "Analyse performance trends", specialist: "performance-analytics" },
      { kind: "specialist_call", label: "Flag missed deadlines", specialist: "task-management", inputs: { action: "overdue" } },
      { kind: "specialist_call", label: "Generate improvement suggestions", specialist: "generate-report", inputs: { format: "weekly_review" } },
      { kind: "emit_event", label: "Publish weekly review event", domain: "ai", type: "weekly_review_completed" },
    ],
  },
  {
    id: "wf-issue-escalation",
    name: "Issue Escalation",
    description: "Assess severity, route to owner, notify stakeholders, and track resolution per escalation SOP.",
    category: "automation",
    domain: "tasks",
    version: 1,
    iconKey: "zap",
    preflight: ["requires_org_context"],
    steps: [
      { kind: "specialist_call", label: "Assess issue severity", specialist: "task-management", inputs: { action: "assess_severity" } },
      { kind: "specialist_call", label: "Route to owner", specialist: "manage-pipeline" },
      { kind: "emit_event", label: "Notify stakeholders", domain: "task", type: "issue_escalated" },
      { kind: "specialist_call", label: "Create resolution tracking task", specialist: "task-management", inputs: { action: "create_resolution_task" } },
    ],
  },
  {
    id: "wf-quick-analysis",
    name: "Quick Data Analysis",
    description: "Analyse recent trends in tasks, revenue, or team performance and surface key insights.",
    category: "analysis",
    domain: "analytics",
    version: 1,
    iconKey: "zap",
    preflight: ["requires_org_context"],
    steps: [
      { kind: "specialist_call", label: "Gather recent activity", specialist: "performance-analytics" },
      { kind: "specialist_call", label: "Run trend analysis", specialist: "analytics" },
      { kind: "specialist_call", label: "Generate insights report", specialist: "generate-report", inputs: { format: "analysis_brief" } },
    ],
  },
];

/** Zod-validated registry — throws at module load if any definition is malformed. */
export const WORKFLOW_REGISTRY: WorkflowDefinition[] = rawDefinitions.map((def) => {
  const result = workflowDefinitionSchema.safeParse(def);
  if (!result.success) {
    throw new Error(`Invalid WorkflowDefinition "${def.id}": ${result.error.message}`);
  }
  return result.data;
});

/** Look up a workflow by its stable id slug. */
export function getWorkflowById(id: string): WorkflowDefinition | undefined {
  return WORKFLOW_REGISTRY.find((wf) => wf.id === id);
}
